/* eslint-disable max-lines */

/* eslint-disable complexity */
import { createReducer } from "@reduxjs/toolkit";
import {
    comparingScenarioChartDataReceived,
    comparingScenarioMapDataReceived,
    comparisonDwellTimeDataReceived,
    demandChartDataReceived,
    demandComparisonChartDataReceived,
    demandFiltersReceived,
    dwellTimeDataReceived,
    getAggregatedDemandMapData,
    getAggregatedDwellTimeData,
    getComparingScenarioChartData,
    getDemandChartAggregateData,
    getDemandChartData,
    getDemandFilters,
    getDwellTimeData,
    getMapData,
    getSupplyChartData,
    getSupplyFilters,
    highlightedDashboardLinks,
    historicalOccupancyReceived,
    mapDataReceived,
    selectedHistoricalOccupancyRange,
    setActiveDashboard,
    setComparisonAPIParams,
    setComparisonNonAPIParams,
    setDemandComparisonNonAPIParams,
    setComparisonSummaryStatsData,
    setDemandSummaryStatsData,
    setLegends,
    setSelectedSupplyBarChartData,
    setSupplyAPIParams,
    setSupplyNonAPIParams,
    setSupplySummaryStatusData,
    storeDemandComparisonAPIParams,
    storeDemandFilterFormValues,
    supplyChartDataReceived,
    supplyFiltersReceived,
    toggleHistoricalOccupancy,
    updateMapDataSlave,
    storeExportChartData,
    downloadExportedFile,
    getAverageOccupancyPercent,
    setChartLegends,
    demandSessionDataReceived,
    getDemandSessionData,
    demandComparisonSessionDataReceived,
    getDemandSessionAggregateData,
    getDemandComparisonChartData,
    getDemandComparisonChartAggregateData,
    getDemandComparisonSessionAggregateData,
    getDemandComparisonSessionData,
    updateDemandFiltersError
} from "actions";
import { resetStateRTK } from "components/curb/ManagerAnalyzer/Toggle/commonUtilities";
import { DashboardStateType, DashboardTypes } from "../actions/Dashboards/types";

const defaultSummaryStatsChange = {
    value: 0,
    change: null,
    percentage: null
};

const initialState: DashboardStateType = {
    activeDashboard: DashboardTypes.Supply,
    comparingScenarioChartData: {},
    comparingScenarioMapData: {
        links: {},
        regulations: {}
    },
    comparisonSummaryStatsData: {
        biggestGainRegulation: {
            name: "",
            spaces: 0,
            time: ""
        },
        biggestLossRegulation: {
            name: "",
            spaces: 0,
            time: ""
        },
        newRegulationsPercentage: 0,
        totalRevenue: 0,
        totalUsers: 0
    },
    demandChartData: {},
    demandSessionData: {},
    demandComparisonSessionData: {},
    demandChartType: "",
    demandComparisonChartData: {},
    demandFilters: {
        demand_data: {},
        source_data: {},
        error: ""
    },
    demandFiltersFormData: {
        demandAPIParams: {
            analysis_zone_id: -1,
            date: "",
            time: ""
        },
        demandNonAPIParams: {
            selectedRegulationTypeIds: [],
            selectedTimePeriod: {
                max: 0,
                min: 0
            },
            selectedOccupancyRange: {
                min: 0,
                max: 100
            },
            occupancyType: "",
            selectedDemandSource: [],
            selectedComparisonDemandSource: [],
            compareType: ""
        }
    },
    demandSummaryStatsData: {
        avgOccupancyRate: defaultSummaryStatsChange,
        avgTotalSessions: defaultSummaryStatsChange,
        maxOccupancyRate: {
            percentage: 0,
            time: ""
        },
        totalRevenue: defaultSummaryStatsChange,
        totalSessions: defaultSummaryStatsChange
    },
    demandComparisonAPIParams: { analysis_zone_id: -1, demand_source: [0] },
    dwellTimeData: {},
    comparisonDwellTimeData: {},
    highlightedDashboardLinkIds: [],
    highlightedComparisonDashboardLinkIds: [],
    isFetchingComparingScenarioChartData: true,
    isFetchingDemandChartData: true,
    isFetchingDemandSessionData: true,
    isFetchingDemandFiltersData: true,
    isFetchingDwellTimeData: true,
    isFetchingSupplyChartData: true,
    isFetchingSupplyFilterData: true,
    isFetchingSupplyMapData: false,
    legends: [],
    chartLegends: [],
    selectedSupplyBarChartData: {
        selectedBarStackIds: {},
        selectedBottomChartBarStackIds: {},
        selectedDwellTime: {}
    },
    supplyAPIParams: {},
    comparisonAPIParams: {},
    supplyChartData: {},
    supplyFilterData: {},
    avgOccupancyPercent: 0,
    supplyMapData: {
        links: {},
        regulations: {}
    },
    supplyMapDataSlave: {},
    supplyNonAPIParams: {},
    comparisonNonAPIParams: {},
    demandComparisonNonAPIParams: {},
    supplySummaryStatusData: {
        mostCommonRegulation: {
            name: "",
            spaces: 0,
            time: ""
        },
        potentialRevenue: 0,
        revenuePercent: 0,
        totalSpaces: 0,
        totalUsers: 0
    },
    isHistoricalOccupancyOn: false,
    historicalData: null,
    startDate: "",
    endDate: "",
    exportChartData: {
        exportTopChartData: {},
        exportBottomChartData: {}
    },
    isDownloading: false,
    fileType: ""
};

const Dashboards = createReducer(initialState, (builder) => {
    builder
        .addCase(getSupplyFilters, (state) => {
            state.isFetchingSupplyFilterData = true;
        })
        .addCase(supplyFiltersReceived, (state, action) => {
            state.isFetchingSupplyFilterData = false;
            state.supplyFilterData = action.payload.data;
        })
        .addCase(getMapData, (state) => {
            state.isFetchingSupplyMapData = true;
        })
        .addCase(getAggregatedDemandMapData, (state) => {
            state.isFetchingSupplyMapData = true;
        })
        .addCase(mapDataReceived, (state, action) => {
            state.isFetchingSupplyMapData = false;
            state.supplyMapData = action.payload.supplyMapDataMaster;
        })
        .addCase(getSupplyChartData, (state) => {
            state.isFetchingSupplyChartData = true;
        })
        .addCase(supplyChartDataReceived, (state, action) => {
            state.isFetchingSupplyChartData = false;
            state.supplyChartData = action.payload.data;
        })
        .addCase(setSupplyAPIParams, (state, action) => {
            state.supplyAPIParams = {
                ...state.supplyAPIParams,
                ...action.payload.data
            };
        })
        .addCase(downloadExportedFile, (state, action) => {
            state.isDownloading = action.payload.isDownloading;
            state.fileType = action.payload.fileType ?? state.fileType;
        })
        .addCase(storeExportChartData, (state, action) => {
            state.exportChartData.exportTopChartData =
                action.payload?.exportTopChartData || state.exportChartData.exportTopChartData;
            state.exportChartData.exportBottomChartData =
                action.payload?.exportBottomChartData ||
                state.exportChartData.exportBottomChartData;
        })
        .addCase(setComparisonAPIParams, (state, action) => {
            state.comparisonAPIParams = {
                ...state.comparisonAPIParams,
                ...action.payload.data
            };
        })
        .addCase(setSupplyNonAPIParams, (state, action) => {
            state.supplyNonAPIParams = action.payload.data;
        })
        .addCase(setComparisonNonAPIParams, (state, action) => {
            state.comparisonNonAPIParams = action.payload.data;
        })
        .addCase(setDemandComparisonNonAPIParams, (state, action) => {
            state.demandComparisonNonAPIParams = action.payload.data;
        })
        .addCase(updateMapDataSlave, (state, action) => {
            state.supplyMapDataSlave = action.payload.supplyMapDataSlave;
        })
        .addCase(highlightedDashboardLinks, (state, action) => {
            state.highlightedDashboardLinkIds =
                action.payload?.highlightedDashboardLinkIds || state.highlightedDashboardLinkIds;
            state.highlightedComparisonDashboardLinkIds =
                action.payload?.highlightedComparisonDashboardLinkIds ||
                state.highlightedComparisonDashboardLinkIds;
        })
        .addCase(setLegends, (state, action) => {
            state.legends = action.payload.legends;
        })
        .addCase(setChartLegends, (state, action) => {
            state.chartLegends = action.payload.legends;
        })
        .addCase(setSelectedSupplyBarChartData, (state, action) => {
            state.selectedSupplyBarChartData = action.payload.selectedSupplyBarChartData;
        })
        .addCase(setSupplySummaryStatusData, (state, action) => {
            state.supplySummaryStatusData = {
                ...state.supplySummaryStatusData,
                ...action.payload.supplySummaryStatusData
            };
        })
        .addCase(getDemandChartData, (state) => {
            state.isFetchingDemandChartData = true;
        })
        .addCase(getDemandComparisonChartData, (state) => {
            state.isFetchingDemandChartData = true;
        })
        .addCase(getDemandChartAggregateData, (state) => {
            state.isFetchingDemandChartData = true;
        })
        .addCase(getDemandComparisonChartAggregateData, (state) => {
            state.isFetchingDemandChartData = true;
        })
        .addCase(getDemandSessionData, (state) => {
            state.isFetchingDemandSessionData = true;
        })
        .addCase(getDemandSessionAggregateData, (state) => {
            state.isFetchingDemandSessionData = true;
        })
        .addCase(getDemandComparisonSessionData, (state) => {
            state.isFetchingDemandSessionData = true;
        })
        .addCase(getDemandComparisonSessionAggregateData, (state) => {
            state.isFetchingDemandSessionData = true;
        })
        .addCase(demandSessionDataReceived, (state, action) => {
            state.isFetchingDemandSessionData = false;
            state.demandSessionData = action.payload.demandSessionData;
        })
        .addCase(demandComparisonSessionDataReceived, (state, action) => {
            state.isFetchingDemandSessionData = false;
            state.demandComparisonSessionData = action.payload.demandComparisonSessionData;
        })
        .addCase(demandChartDataReceived, (state, action) => {
            state.demandChartData = action.payload.demandChartData;
            state.demandChartType = action.payload.type;
            state.isFetchingDemandChartData = false;
        })
        .addCase(demandComparisonChartDataReceived, (state, action) => {
            state.demandComparisonChartData = action.payload.demandComparisonChartData;
            state.isFetchingDemandChartData = false;
        })
        .addCase(getDemandFilters, (state) => {
            state.isFetchingDemandFiltersData = true;
        })
        .addCase(demandFiltersReceived, (state, action) => {
            state.demandFilters = action.payload.filters;
            state.isFetchingDemandFiltersData = false;
        })
        .addCase(storeDemandFilterFormValues, (state, action) => {
            state.demandFiltersFormData = action.payload.formData;
        })
        .addCase(storeDemandComparisonAPIParams, (state, action) => {
            state.demandComparisonAPIParams = action.payload.data;
        })
        .addCase(setActiveDashboard, (state, action) => {
            state.activeDashboard = action.payload.activeDashboard;
        })
        .addCase(setDemandSummaryStatsData, (state, action) => {
            state.demandSummaryStatsData = {
                ...state.demandSummaryStatsData,
                ...action.payload.demandSummaryStatsData
            };
        })
        .addCase(setComparisonSummaryStatsData, (state, action) => {
            state.comparisonSummaryStatsData = {
                ...state.comparisonSummaryStatsData,
                ...action.payload.comparisonSummaryStatsData
            };
        })
        .addCase(getComparingScenarioChartData, (state) => {
            state.isFetchingComparingScenarioChartData = true;
        })
        .addCase(comparingScenarioChartDataReceived, (state, action) => {
            state.comparingScenarioChartData = action.payload.data;
            state.isFetchingComparingScenarioChartData = false;
        })
        .addCase(comparingScenarioMapDataReceived, (state, action) => {
            state.comparingScenarioMapData = action.payload.supplyMapDataMaster;
        })
        .addCase(getDwellTimeData, (state) => {
            state.isFetchingDwellTimeData = true;
        })
        .addCase(getAggregatedDwellTimeData, (state) => {
            state.isFetchingDwellTimeData = true;
        })
        .addCase(dwellTimeDataReceived, (state, action) => {
            state.dwellTimeData = action.payload.dwellTimeData;
            state.isFetchingDwellTimeData = false;
        })
        .addCase(comparisonDwellTimeDataReceived, (state, action) => {
            state.comparisonDwellTimeData = action.payload.comparisonDwellTimeData;
            state.isFetchingDwellTimeData = false;
        })
        .addCase(toggleHistoricalOccupancy, (state, action) => {
            state.isHistoricalOccupancyOn = action.payload.isHistoricalOccupancyOn;
        })
        .addCase(historicalOccupancyReceived, (state, action) => {
            state.historicalData = action.payload.data;
        })
        .addCase(selectedHistoricalOccupancyRange, (state, action) => {
            state.startDate = action.payload.startDate;
            state.endDate = action.payload.endDate;
        })
        .addCase(getAverageOccupancyPercent, (state, action) => {
            state.avgOccupancyPercent = action.payload.data;
        })
        .addCase(updateDemandFiltersError, (state, action) => {
            state.demandFilters.error = action.payload;
        })
        .addCase(resetStateRTK, () => initialState)
        .addDefaultCase((state) => state);
});

export default Dashboards;
